import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FamilyCatalogueDescription } from '../../components/FamilyCatalogueDescription';
import { publicJSON, peekPublicJSON, isCatalogueNotFound } from '../../lib/public-catalogue-cache';
import { CatalogueLoadError } from '../../components/catalog/CatalogueLoadError';
import { CatalogueIntro } from '../../components/catalog/CatalogueIntro';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage } from '../../components/ui/breadcrumb';
import { toast } from 'sonner';
import { Package, Lightbulb, Zap, Thermometer, Eye, Shield, Palette, Check, X, Download, FileText, Wrench, Share2, Copy, FileDown, Loader2, ChevronRight, Layers, ArrowRight, Droplets, Ruler, Scissors } from 'lucide-react';
import SEO, { generateBreadcrumbSchema, generateFamilyCollectionSchema } from '../../components/SEO';
import { useCatalogueIdentity } from '../../hooks/useCatalogueIdentity';
import { modelSlug } from '../../lib/catalogue-paths';
import { getCollectionSlug } from '../../utils/productUrl';
import SmartBundlePanel from '../../components/catalog/SmartBundlePanel';
import { optimizedSrc, optimizedSrcSet } from '../../lib/image-cdn';
import SpecifierActions from '../../components/specifier/SpecifierActions';
import FamilySpecTable from '../../components/specifier/FamilySpecTable';
import FamilyEditorial from '../../components/products/FamilyEditorial';
import { FamilyModelConfigurator } from '../../components/configuration/FamilyModelConfigurator';
import { FamilyComponents } from '../../components/products/FamilyComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Fade-in animation ── */
const FadeUp = ({ children, className = '', delay = 0, ...props }) => (
  <div className={`animate-fadeUp ${className}`} style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }} {...props}>
    {children}
  </div>
);

/* ── Product image with white bg + fallback ── */
const IMAGE_VERSION = 'v2'; // Cache-buster for replaced images
const normalizeImagePath = (src) => {
  if (!src) return src;
  if (src.startsWith('/files/')) return `/product-images/${src.split('/files/').pop()}?${IMAGE_VERSION}`;
  if (src.includes('erp.ihgind.com') && src.includes('/files/')) {
    const filename = src.split('/files/').pop();
    return `/product-images/${filename}?${IMAGE_VERSION}`;
  }
  if (src.startsWith('/product-images/')) return `${src}?${IMAGE_VERSION}`;
  return src;
};

const ProductImage = ({ src, alt, className = '' }) => {
  const [err, setErr] = useState(false);
  const imgSrc = normalizeImagePath(src);
  if (!imgSrc || err) return <div className={`w-full h-full flex items-center justify-center bg-white ${className}`} data-image-surface="product"><Lightbulb className="w-8 h-8 text-[#CCC]" /></div>;
  return (
    <img
      src={optimizedSrc(imgSrc, { w: 900 })}
      srcSet={optimizedSrcSet(imgSrc, [400, 640, 900, 1400])}
      sizes="(max-width: 768px) 92vw, 640px"
      alt={alt}
      width={900}
      height={900}
      className={`w-full h-full object-contain ${className}`}
      loading="lazy"
      decoding="async"
      onError={() => setErr(true)}
    />
  );
};

/* ── Helpers ── */
// CCT is the only multi-valued variant attribute (a single SKU row can list
// several Kelvin options e.g. [3000,4000,5000]) — matching it against a
// single selected value must check array membership, not string equality.
// Using equality (the old behaviour) meant a multi-CCT variant could never
// match any individual CCT button, which is why every CCT option rendered
// permanently disabled on multi-CCT families.
function variantMatchesAttr(v, attr, val) {
  if (val === '' || val === null || val === undefined) return true;
  if (attr === 'cct') return (v.cct_values || []).map(String).includes(String(val));
  if (attr === 'color') return (v.color_values || []).map(String).includes(String(val));
  if (attr === 'finish') return (v.body_finish_values || [v.body_finish_normalized]).filter(Boolean).map(String).includes(String(val));
  return String(getVariantAttr(v, attr)) === String(val);
}

function getAvailableOptions(variants, selections) {
  const attrs = ['power', 'finish', 'beam', 'ip', 'cct', 'color'];
  const result = {};
  for (const attr of attrs) {
    const filtered = variants.filter(v => {
      for (const [key, val] of Object.entries(selections)) {
        if (key === attr || !val) continue;
        if (!variantMatchesAttr(v, key, val)) return false;
      }
      return true;
    });
    const values = (attr === 'cct' || attr === 'color')
      ? filtered.flatMap(v => (v[`${attr}_values`] || []).map(String))
      : attr === 'finish'
        ? filtered.flatMap(v => (v.body_finish_values || [v.body_finish_normalized]).filter(Boolean).map(String))
        : filtered.map(v => getVariantAttr(v, attr)).filter(x => x !== null && x !== undefined && x !== '');
    result[attr] = [...new Set(values)].sort((a, b) => {
      const na = parseFloat(a), nb = parseFloat(b);
      return isNaN(na) || isNaN(nb) ? String(a).localeCompare(String(b)) : na - nb;
    });
  }
  return result;
}

function getVariantAttr(v, attr) {
  const map = {
    power: v.power_numeric,
    finish: v.body_finish_normalized,
    beam: v.beam_angle_numeric,
    ip: v.ip_rating_normalized,
    color: v.color,
    cct: v.cct_values?.length === 1 ? v.cct_values[0] : (v.cct_values?.join(',') || null),
  };
  return map[attr] ?? null;
}

function formatAttrLabel(attr, value) {
  if (value === null || value === undefined) return '';
  switch (attr) {
    case 'power': return `${value}W`;
    case 'beam': return `${value}°`;
    case 'cct': return String(value).includes(',') ? value.split(',').map(c => `${c}K`).join('/') : `${value}K`;
    case 'ip': return value;
    case 'color': return String(value).toUpperCase();
    case 'finish': return String(value).charAt(0).toUpperCase() + String(value).slice(1);
    default: return String(value);
  }
}

export default function FamilyListingPage() {
  const identity = useCatalogueIdentity();
  const { collection: collectionParam, family: familyParam } = useParams();
  const dbCollection = getCollectionSlug(collectionParam);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const familyResource = `/search/family/${familyParam}?collection=${dbCollection}${searchParams.get('category') ? `&category=${encodeURIComponent(searchParams.get('category'))}` : ''}`;
  const [data, setData] = useState(() => peekPublicJSON(familyResource) || null);
  const [loading, setLoading] = useState(() => !peekPublicJSON(familyResource));
  const [loadedResource, setLoadedResource] = useState(familyResource);
  const [error, setError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [selections, setSelections] = useState({ power: '', finish: '', beam: '', ip: '', cct: '', color: '' });
  const [skuFilter, setSkuFilter] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [datasheets, setDatasheets] = useState({ family_datasheets: [], variant_datasheets: [], total: 0 });
  const [accessories, setAccessories] = useState({ required: [], recommended: [], compatible: [], total: 0 });
  const [showShareSummary, setShowShareSummary] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [editorial, setEditorial] = useState(null);
  const [guide, setGuide] = useState(null);

  // Section 6: reviewed editorial copy where it exists for this family
  useEffect(() => {
    setEditorial(null);
    if (!familyParam || !identity?.editorial_available || !identity.path.endsWith('/' + familyParam)) return;
    let live = true;
    fetch(`${BACKEND_URL}/api/search/family/${familyParam}/editorial`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => live && setEditorial(d?.editorial || null))
      .catch(() => live && setEditorial(null));
    return () => { live = false; };
  }, [familyParam, identity?.editorial_available, identity?.path]);

  useEffect(() => {
    if (!familyParam) return;
    let live = true;
    fetch(`${BACKEND_URL}/api/search/family/${familyParam}/guide`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => live && setGuide(d && d.url ? d : null))
      .catch(() => live && setGuide(null));
    return () => { live = false; };
  }, [familyParam]);

  useEffect(() => {
    if (!familyParam) return;
    let live = true;
    const fetchFamily = async () => {
      setLoadedResource(familyResource);
      if (!peekPublicJSON(familyResource)) setLoading(true);
      setError(null);
      try {
        const result = await publicJSON(familyResource);
        if (!live) return;
        // Redirect if the URL collection doesn't match the product's actual collection
        const correctCollection = result?.parent?.primary_collection;
        if (correctCollection && collectionParam && correctCollection !== dbCollection) {
          const urlCollection = correctCollection.replace(/^lumi-/, 'lumi-');
          navigate(`/${urlCollection}/${familyParam}`, { replace: true });
          return;
        }
        setData(result);
      } catch (err) {
        if (!live) return;
        setError(err);
        toast.error(`Failed to load ${familyParam}`);
      } finally {
        if (live) setLoading(false);
      }
    };
    fetchFamily();
    return () => { live = false; };
  }, [familyParam, familyResource, dbCollection, collectionParam, navigate, loadAttempt]);

  useEffect(() => {
    if (!data?.parent?.canonical_slug) return;
    fetch(`${BACKEND_URL}/api/search/datasheets/family/${data.parent.canonical_slug}`)
      .then(r => r.ok ? r.json() : { family_datasheets: [], variant_datasheets: [], total: 0 })
      .then(setDatasheets).catch(() => {});
  }, [data]);

  useEffect(() => {
    if (!data?.parent?.canonical_slug) return;
    fetch(`${BACKEND_URL}/api/search/compatible-accessories/${data.parent.canonical_slug}`)
      .then(r => r.ok ? r.json() : { required: [], recommended: [], compatible: [], total: 0 })
      .then(setAccessories).catch(() => {});
  }, [data]);

  useEffect(() => {
    const init = {};
    for (const key of ['power', 'finish', 'beam', 'ip', 'cct', 'color']) init[key] = searchParams.get(key) || '';
    setSelections(init);
    setSkuFilter(searchParams.get('sku') || '');
  }, [searchParams]);

  const parent = data?.parent;
  const isDisambiguation = data?.disambiguation === true;
  const variants = useMemo(() => data?.variants || [], [data]);
  const attrOptions = data?.attribute_options || {};

  // Spec summary for schema.org additionalProperty (audit: was always empty)
  const schemaSpecs = [
    attrOptions.power?.length > 0 && { label: 'Wattage', value: [...attrOptions.power].sort((a, b) => a - b).map(p => `${p}W`).join(' / ') },
    attrOptions.cct?.length > 0 && { label: 'Colour Temperature (CCT)', value: [...attrOptions.cct].map(c => `${c}K`).join(' / ') },
    attrOptions.color?.length > 0 && { label: 'Colour', value: [...attrOptions.color].join(' / ') },
    attrOptions.ip_rating?.length > 0 && { label: 'IP Rating', value: [...attrOptions.ip_rating].join(' / ') },
    attrOptions.beam_angle?.length > 0 && { label: 'Beam Angle', value: [...attrOptions.beam_angle].sort((a, b) => a - b).map(b => `${b}°`).join(' / ') },
    attrOptions.cri?.length > 0 && { label: 'CRI (Colour Rendering Index)', value: [...attrOptions.cri].join(' / ') },
    attrOptions.body_finish?.length > 0 && { label: 'Finish', value: [...attrOptions.body_finish].join(' / ') },
    attrOptions.dimension?.length > 0 && { label: 'Dimensions', value: [...attrOptions.dimension].join(' / ') },
    attrOptions.cut_out?.length > 0 && { label: 'Cut-Out', value: [...attrOptions.cut_out].join(' / ') },
  ].filter(Boolean);
  const siblings = data?.sibling_families || [];
  // Detect multi-article from backend flag OR from variant data (multiple unique base_codes)
  const uniqueBaseCodes = useMemo(() => new Set(variants.map(v => v.base_code).filter(Boolean)), [variants]);
  const isMultiArticle = data?.multi_article_family === true || uniqueBaseCodes.size > 1;

  const articles = useMemo(() => {
    if (!isMultiArticle) return null;
    const grouped = {};
    for (const v of variants) {
      const bc = v.base_code || 'unknown';
      if (!grouped[bc]) {
        grouped[bc] = {
          base_code: bc, product_id: v.product_id, name: v.name, primary_image: v.primary_image,
          collection_slug: v.collection_slug,
          variants: [], powers: new Set(), ccts: new Set(), beams: new Set(), cris: new Set(), ips: new Set(), finishes: new Set(), colors: new Set(), dims: new Set(),
        };
      }
      const g = grouped[bc];
      g.variants.push(v);
      if (v.dimension) g.dims.add(v.dimension);
      if (v.power_numeric) g.powers.add(v.power_numeric);
      if (v.cct_values) v.cct_values.forEach(c => g.ccts.add(c));
      if (v.beam_angle_numeric) g.beams.add(v.beam_angle_numeric);
      if (v.cri_numeric) g.cris.add(v.cri_numeric);
      if (v.ip_rating_normalized) g.ips.add(v.ip_rating_normalized);
      if (v.body_finish_normalized) g.finishes.add(v.body_finish_normalized);
      if (v.color_values) v.color_values.forEach(c => g.colors.add(c));
    }
    return Object.values(grouped).map(g => ({
      ...g,
      sku_count: g.variants.reduce((sum, v) => sum + (v.skus?.length || 1), 0),
    })).sort((a, b) => a.base_code.localeCompare(b.base_code));
  }, [variants, isMultiArticle]);

  // Total SKU count across all article cards — must match the sum shown on
  // the cards below, not the raw doc count (data.variant_count), which only
  // counts one row per article after the legacy-duplicate cleanup.
  const totalSkuCount = articles ? articles.reduce((sum, a) => sum + a.sku_count, 0) : (data?.variant_count || 0);

  // Detect if family spans multiple collections (for showing badges)
  const hasMultipleCollections = useMemo(() => {
    const cols = new Set(variants.map(v => v.collection_slug).filter(Boolean));
    return cols.size > 1;
  }, [variants]);

  const availableOptions = useMemo(() => getAvailableOptions(variants, selections), [variants, selections]);
  const matchedVariants = useMemo(() => {
    return variants.filter(v => {
      if (skuFilter && String(v.base_code).toUpperCase() !== skuFilter.toUpperCase()) return false;
      for (const [key, val] of Object.entries(selections)) {
        if (!val) continue;
        if (!variantMatchesAttr(v, key, val)) return false;
      }
      return true;
    });
  }, [variants, selections, skuFilter]);

  useEffect(() => {
    setSelectedVariant(matchedVariants.length === 1 ? matchedVariants[0] : null);
  }, [matchedVariants]);

  // For multi-article families, narrow the Product Lines grid to only the
  // articles that still have at least one variant matching the current spec
  // selections (gives multi-article families a working configurator too).
  const filteredArticles = useMemo(() => {
    if (!articles) return articles;
    const matchedCodes = new Set(matchedVariants.map(v => v.base_code));
    return articles.filter(a => matchedCodes.has(a.base_code));
  }, [articles, matchedVariants]);

  // Hide spec columns entirely for accessory/passive families with no data
  // in any variant, rather than showing a column of all-dashes.
  const showPowerCol = useMemo(() => variants.some(v => v.power_numeric), [variants]);
  const showCctCol = useMemo(() => variants.some(v => v.cct_values?.length > 0 || v.color_values?.length > 0), [variants]);
  const showBeamCol = useMemo(() => variants.some(v => v.beam_angle_numeric), [variants]);
  const showCriCol = useMemo(() => variants.some(v => v.cri_numeric), [variants]);
  const showFinishCol = useMemo(() => variants.some(v => v.body_finish_normalized), [variants]);
  const showSizeCol = useMemo(() => variants.some(v => v.dimension), [variants]);
  const visibleColCount = 2 + [showPowerCol, showCctCol, showBeamCol, showCriCol, showFinishCol, showSizeCol].filter(Boolean).length;

  const handleSelect = useCallback((attr, value) => {
    const next = { ...selections, [attr]: selections[attr] === value ? '' : value };
    setSelections(next);
    setSkuFilter('');
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(next)) { if (v) params.set(k, v); }
    setSearchParams(params, { replace: true });
  }, [selections, setSearchParams]);

  const clearSelections = useCallback(() => {
    setSelections({ power: '', finish: '', beam: '', ip: '', cct: '', color: '' });
    setSkuFilter('');
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const hasSelections = Object.values(selections).some(v => v) || !!skuFilter;

  // Dimension drawing — prefer the selected variant, else the first article that has one
  const dimensionInfo = useMemo(() => {
    const src = (selectedVariant?.dimension_diagram && selectedVariant)
      || variants.find(v => v.dimension_diagram)
      || selectedVariant
      || variants.find(v => v.dimension)
      || parent
      || {};
    const totalModels = new Set(variants.map(v => v.base_code).filter(Boolean)).size;
    return {
      diagram: src.dimension_diagram,
      dimension: src.dimension,
      cutOut: src.cut_out,
      // audit DATA04: never present one model's drawing as a family-wide size
      modelCode: src.base_code || src.sku_code,
      isModelSpecific: totalModels > 1,
      modelCount: totalModels,
    };
  }, [selectedVariant, variants, parent]);

  // Build gallery images: product cutouts first, then application/scene images
  const galleryImages = useMemo(() => {
    const seen = new Set();
    const productImgs = [];
    const sceneImgs = [];
    const otherImgs = [];

    const addImage = (src) => {
      // Handle both string and object image formats
      const imgUrl = typeof src === 'object' && src?.url ? src.url : src;
      if (!imgUrl || typeof imgUrl !== 'string' || seen.has(imgUrl)) return;
      seen.add(imgUrl);
      const lower = imgUrl.toLowerCase();
      if (lower.includes('_product.')) productImgs.push(imgUrl);
      else if (lower.includes('_scene.') || lower.includes('_app.') || lower.includes('application')) sceneImgs.push(imgUrl);
      else otherImgs.push(imgUrl);
    };

    // Prioritize selected variant images
    const source = selectedVariant || parent;
    if (source?.primary_image) addImage(source.primary_image);
    if (source?.hero_image) addImage(source.hero_image);
    if (source?.images) source.images.forEach(addImage);
    if (source?.display_image) addImage(source.display_image);

    // Also pull from all variants for more coverage
    variants.forEach(v => {
      if (v.primary_image) addImage(v.primary_image);
      if (v.images) v.images.forEach(addImage);
    });

    return [...productImgs, ...otherImgs, ...sceneImgs];
  }, [selectedVariant, parent, variants]);

  // Reset active image when variant changes
  useEffect(() => { setActiveImageIndex(0); }, [selectedVariant]);

  const collectionRoute = collectionParam || parent?.primary_collection || '';
  const collectionLabel = collectionRoute.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const getShareUrl = useCallback(() => {
    const base = `${window.location.origin}/${collectionRoute}/${parent?.canonical_slug || familyParam}`;
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(selections)) { if (v) params.set(k, v); }
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }, [collectionRoute, parent, familyParam, selections]);

  const copyShareUrl = useCallback(() => {
    navigator.clipboard.writeText(getShareUrl()).then(() => toast.success('Configuration URL copied')).catch(() => toast.error('Failed to copy'));
  }, [getShareUrl]);

  const downloadProductPdf = useCallback(async () => {
    if (!parent?.product_id) return;
    setGeneratingPdf(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/${parent.product_id}/general-datasheet`);
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${parent.family || parent.base_code}_datasheet.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to generate PDF'); }
    finally { setGeneratingPdf(false); }
  }, [parent]);

  const downloadVariantPdf = useCallback(async (variant) => {
    if (!variant?.product_id) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/${variant.product_id}/general-datasheet`);
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${variant.base_code || variant.name}_datasheet.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to generate variant PDF'); }
  }, []);

  // Configurator attrs
  const configuratorAttrs = [
    { key: 'power', label: 'Power', icon: Zap, options: attrOptions.power || [] },
    { key: 'beam', label: 'Beam Angle', icon: Eye, options: attrOptions.beam_angle || [] },
    { key: 'cct', label: 'CCT', icon: Thermometer, options: attrOptions.cct || [] },
    { key: 'color', label: 'Colour', icon: Droplets, options: attrOptions.color || [] },
    { key: 'finish', label: 'Finish', icon: Palette, options: attrOptions.body_finish || [] },
    { key: 'ip', label: 'IP Rating', icon: Shield, options: attrOptions.ip_rating || [] },
  ].filter(a => a.options.length > 0);

  // Shared configurator UI — rendered identically on single-article and
  // multi-article family pages so the configuration experience is
  // consistent across every Lumi-Flex family template.
  const configuratorBlock = configuratorAttrs.length > 0 && (
    <div data-testid="variant-configurator">
      <FadeUp>
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-1">Configuration</p>
            <h2 className="text-2xl tracking-tight uppercase" style={{ fontFamily: 'var(--font-display)' }}>
              <span style={{ fontWeight: 300 }}>Select </span><span style={{ fontWeight: 700 }}>Specification</span>
            </h2>
          </div>
          {hasSelections && (
            <div className="flex gap-2">
              <Button onClick={clearSelections} className="bg-transparent border border-[#E8E8E8] text-[#999] hover:bg-[#FAFAFA] rounded-none px-4 py-2 text-[10px] font-bold uppercase tracking-wider" data-testid="clear-all-selections">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
              <Button onClick={() => setShowShareSummary(!showShareSummary)} className="bg-transparent border border-[#E8E8E8] text-[#999] hover:bg-[#FAFAFA] rounded-none px-4 py-2 text-[10px] font-bold uppercase tracking-wider" data-testid="share-config-btn">
                <Share2 className="w-3 h-3 mr-1" /> Share
              </Button>
            </div>
          )}
        </div>
      </FadeUp>

      {/* Deep-link banner — landed here via a suggested/shared exact SKU link */}
      {skuFilter && (
        <div className="mb-6 flex items-center justify-between bg-[#F5F9FF] border border-[#D8E6FA] rounded-lg p-3" data-testid="sku-filter-banner">
          <p className="text-xs text-[#3A6FB0]">Showing only <span className="font-bold">{skuFilter}</span></p>
          <button onClick={clearSelections} className="text-xs text-[#3A6FB0] underline" data-testid="clear-sku-filter">View all specifications</button>
        </div>
      )}

      {/* Unavailability warning */}
      {hasSelections && matchedVariants.length === 0 && variants.length > 0 && (
        <div className="mb-6 bg-[#FFF8F0] border border-[#F0E0C8] rounded-lg p-4" data-testid="unavailable-warning">
          <p className="text-sm font-semibold text-[#8B6914]">Configuration unavailable</p>
          <p className="text-xs text-[#A08040] mt-1">Try adjusting your selection below.</p>
        </div>
      )}

      <div className="space-y-6">
        {configuratorAttrs.map(({ key, label, icon: Icon, options }, attrIdx) => {
          const available = new Set(availableOptions[key]?.map(String) || []);
          return (
            <FadeUp key={key} delay={attrIdx * 80} data-testid={`configurator-row-${key}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-3.5 h-3.5 text-[#BBB]" />
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#999]">{label}</span>
                {selections[key] && <Badge className="h-5 text-[10px] bg-[#1A1A1A] text-white border-0 rounded-sm">{formatAttrLabel(key, selections[key])}</Badge>}
              </div>
              <div className="flex flex-wrap gap-2">
                {options.map(opt => {
                  const strOpt = String(opt);
                  const isSelected = String(selections[key]) === strOpt;
                  const isAvailable = available.has(strOpt);
                  return (
                    <button
                      key={strOpt}
                      onClick={() => isAvailable ? handleSelect(key, strOpt) : null}
                      disabled={!isAvailable}
                      className={`px-4 py-2 text-xs font-medium border transition-all duration-200 ${
                        isSelected
                          ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                          : isAvailable
                            ? 'bg-white border-[#E8E8E8] hover:border-[#999] text-[#1A1A1A]'
                            : 'bg-[#FAFAFA] border-[#F0F0F0] text-[#CCC] cursor-not-allowed line-through'
                      }`}
                      data-testid={`opt-${key}-${strOpt}`}
                    >
                      {formatAttrLabel(key, opt)}
                    </button>
                  );
                })}
              </div>
            </FadeUp>
          );
        })}
      </div>

      {/* Match indicator */}
      <div className="mt-6 pt-6 border-t border-[#F0F0F0]" data-testid="match-indicator">
        {selectedVariant ? (
          <p className="text-sm font-medium flex items-center gap-2">
            <Check className="w-4 h-4 text-green-600" />
            <span>Exact match: <span className="font-bold">{selectedVariant.base_code}</span></span>
          </p>
        ) : hasSelections ? (
          <p className="text-sm text-[#777]">{matchedVariants.length} variant{matchedVariants.length !== 1 ? 's' : ''} match your selection</p>
        ) : (
          <p className="text-sm text-[#999]">Select attributes to narrow down variants</p>
        )}
      </div>

      {/* Smart Bundle — driver/connector/end-cap suggestions for the exact SKU selected */}
      {selectedVariant && parent?.primary_collection === 'lumi-flex' && (
        <SmartBundlePanel baseCode={selectedVariant.base_code} />
      )}

      {/* Share Summary */}
      {showShareSummary && hasSelections && (
        <div className="mt-6 bg-[#FAFAFA] border border-[#EAEAEA] rounded-lg p-6" data-testid="share-summary-block">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#999]">Share Configuration</p>
            <Button onClick={copyShareUrl} className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-none px-4 py-2 text-[10px] font-bold uppercase tracking-wider" data-testid="copy-share-url-btn">
              <Copy className="w-3 h-3 mr-1.5" /> Copy Link
            </Button>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-[#999] mb-1">Product</p>
              <p className="text-sm font-semibold">{parent.family} — {parent.primary_category_name || parent.primary_category}</p>
            </div>
            {selectedVariant && (
              <div>
                <p className="text-xs text-[#999] mb-1">Variant</p>
                <p className="text-sm font-bold">{selectedVariant.base_code}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-[#999] mb-1">Specification</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(selections).filter(([, v]) => v).map(([k, v]) => (
                  <span key={k} className="text-[10px] font-medium bg-[#F0F0F0] text-[#666] px-2 py-1 rounded">
                    {k.charAt(0).toUpperCase() + k.slice(1)}: {formatAttrLabel(k, v)}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-white border border-[#E8E8E8] rounded p-3 font-mono text-xs text-[#777] break-all select-all">
              {getShareUrl()}
            </div>
          </div>
        </div>
      )}
    </div>
  );


  // ─── LOADING ───
  if (loading || loadedResource !== familyResource) {
    return (
      <div className="min-h-screen bg-white" style={{ paddingTop: '72px' }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
          <Skeleton className="h-4 w-48 mb-6 bg-gray-100" />
          <Skeleton className="h-12 w-80 mb-4 bg-gray-100" />
          <Skeleton className="h-4 w-64 mb-12 bg-gray-100" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="aspect-square rounded-lg bg-gray-100" />)}
          </div>
        </div>
      </div>
    );
  }

  // ─── ERROR ───
  if (error && !isCatalogueNotFound(error)) {
    return <CatalogueLoadError kind="family" onRetry={() => setLoadAttempt(value => value + 1)} />;
  }
  if (error || (!parent && !isDisambiguation)) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" style={{ paddingTop: '72px' }}>
        <SEO title="Family Not Found | LUMIBRIGHT" description="This product family does not exist." robots="noindex, follow" />
        <div className="text-center" data-testid="family-not-found">
          <Package className="w-16 h-16 mx-auto mb-4 text-[#CCC]" />
          <h2 className="text-lg font-semibold mb-2 text-[#1A1A1A]">Family Not Found</h2>
          <p className="text-sm text-[#999] mb-6">The product family "{familyParam?.replace(/-/g, ' ')}" is no longer available in this collection.</p>
          <div className="flex items-center justify-center gap-4">
            {collectionParam && (
              <Button onClick={() => navigate(`/${collectionParam}`)} className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-none px-6 py-3 text-xs font-bold uppercase tracking-wider" data-testid="browse-collection-btn">
                Browse Collection
              </Button>
            )}
            <Button onClick={() => navigate('/products')} variant="outline" className="rounded-none px-6 py-3 text-xs font-bold uppercase tracking-wider" data-testid="all-products-btn">
              All Products
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── DISAMBIGUATION ───
  if (isDisambiguation) {
    const matches = data.matches || [];
    return (
      <div className="min-h-screen bg-white" style={{ paddingTop: '72px' }} data-testid="family-disambiguation">
        <CatalogueIntro scope={{collection: collectionParam, title: familyParam.replace(/-/g, ' ').toUpperCase()}} titleTestId="family-name" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
          <SEO title={`${familyParam.replace(/-/g, ' ').toUpperCase()} Families - ${collectionLabel}`} description={`${matches.length} product families matching "${familyParam}".`} />
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/" className="text-xs text-[#999]">Home</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator className="text-[#DDD]" />
              <BreadcrumbItem><BreadcrumbLink href="/products" className="text-xs text-[#999]">Products</BreadcrumbLink></BreadcrumbItem>
              {collectionParam && (<><BreadcrumbSeparator className="text-[#DDD]" /><BreadcrumbItem><BreadcrumbLink href={`/${collectionParam}`} className="text-xs text-[#999]">{collectionLabel}</BreadcrumbLink></BreadcrumbItem></>)}
              <BreadcrumbSeparator className="text-[#DDD]" />
              <BreadcrumbItem><BreadcrumbPage className="text-xs font-medium text-[#1A1A1A]">{familyParam.replace(/-/g, ' ').toUpperCase()}</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <p className="text-[#777] mb-12" data-testid="family-disambiguation-count">{matches.length} product families found. Select one:</p>
          <div className="balanced-grid article-grid" data-testid="family-disambiguation-grid">
            {matches.map((m) => (
              <Link key={m.canonical_slug} to={`/${m.primary_collection || collectionParam}/${m.canonical_slug}`} className="group" data-testid={`disambig-card-${m.canonical_slug}`}>
                <div className="bg-white border border-[#EAEAEA] rounded-lg overflow-hidden hover:shadow-lg hover:border-[#DDD] transition-all duration-300">
                  <div className="aspect-square bg-[#F8F8F8] p-4 flex items-center justify-center">
                    <ProductImage
                      src={m.primary_image}
                      alt={`LUMIBRIGHT ${m.family} ${(m.primary_category_name || m.primary_category || 'luminaire').toLowerCase()} product view`}
                      className="p-2"
                    />
                  </div>
                  <div className="p-3 border-t border-[#F0F0F0]">
                    <p className="text-xs font-bold text-[#1A1A1A] tracking-wide">{m.family}</p>
                    <p className="text-[10px] text-[#999] mt-0.5">{m.primary_category_name || ''}</p>
                    <Badge className="mt-2 bg-[#1A1A1A] text-white border-0 rounded-sm text-[9px] px-2 py-0.5">{m.variant_count} variants</Badge>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const displayName = (parent.family || '').toUpperCase();
  const displayNameParts = displayName.split(' ');

  // When the URL encodes an exact selected/shared SKU, render SKU-specific
  // rich-preview metadata (title/description/image) instead of the generic
  // family metadata, so a shared configurator link unfurls with the exact
  // product image + specs in WhatsApp/email/social previews.
  const buildVariantSeo = (variant) => {
    if (!variant) return null;
    const specBits = [
      variant.power_numeric ? `${variant.power_numeric}W` : null,
      variant.cct_values?.length > 0 ? variant.cct_values.map(c => `${c}K`).join('/') : (variant.color ? variant.color : null),
      variant.ip_rating_normalized || null,
      variant.body_finish_normalized || null,
    ].filter(Boolean);
    const specText = specBits.length > 0 ? ` ${specBits.join(' · ')}` : '';
    const rawImage = variant.primary_image || parent.primary_image;
    return {
      title: `${variant.base_code} — ${parent.family}${specText} | ${collectionLabel}`,
      description: specBits.length > 0
        ? `${parent.family} ${variant.base_code}: ${specBits.join(', ')}. Configured on LUMIBRIGHT.`
        : `${parent.family} ${variant.base_code}. Configured on LUMIBRIGHT.`,
      image: rawImage ? encodeURI(rawImage) : rawImage,
    };
  };
  // Audit C3: the family snippet was "FUSION LED Spot Light. 23 product lines,
  // 375 variants." — compose a real buying summary from the family's own data.
  const familySummary = (() => {
    if (!parent) return '';
    const rows = articles || [];
    const label = (parent.primary_category_name || parent.primary_category || 'luminaire').toLowerCase();
    const mounting = (parent.mounting_type_slug || '').replace(/-/g, ' ');
    const list = (values) => [...new Set((values || []).filter(Boolean))];
    const powers = list(rows.map(a => a.power || a.wattage));
    const ccts = list(rows.flatMap(a => a.cct_values || [a.color_temp]));
    const beams = list(rows.map(a => a.beam_angle));
    const ips = list(rows.map(a => a.ip_rating));
    const cutouts = list(rows.map(a => a.cut_out));
    const bits = [];
    if (powers.length) bits.push(`${powers.length > 1 ? `${powers.length} outputs from ${powers[0]}` : powers[0]}`);
    if (ccts.length) bits.push(`${ccts.slice(0, 4).join('/')}`);
    if (beams.length) bits.push(`${beams.slice(0, 3).join('/')} beams`);
    if (ips.length) bits.push(ips.slice(0, 2).join('/'));
    if (cutouts.length) bits.push(`cut-outs ${cutouts.slice(0, 2).join(', ')}`);
    const count = rows.length || 1;
    const head = `${parent.family} is a ${mounting ? mounting + ' ' : ''}${label} ${count > 1 ? `family with ${count} articles` : 'luminaire'}`;
    return `${head}${bits.length ? ' — ' + bits.join(', ') : ''}. Compare variants, dimensions and downloads, then request project pricing.`;
  })();

  const variantSeo = buildVariantSeo(selectedVariant);

  // ─── MULTI-ARTICLE FAMILY ───
  if (isMultiArticle && articles?.length > 0) {
    return (
      <div className="bg-white text-[#1A1A1A] min-h-screen" data-testid="family-detail-page">
        <SEO
          identity={identity}
          title={variantSeo?.title || `${parent.family} - ${parent.primary_category_name || parent.primary_category} - ${collectionLabel}`}
          description={variantSeo?.description || familySummary}
          image={variantSeo?.image || parent.primary_image}
          schema={[
            generateBreadcrumbSchema([
              { name: 'Home', url: `${window.location.origin}/` },
              { name: 'Products', url: `${window.location.origin}/products` },
              { name: collectionLabel, url: `${window.location.origin}/${collectionRoute}` },
              { name: parent.family, url: window.location.href },
            ]),
            // SCHEMA01: family pages describe a set of separate models
            generateFamilyCollectionSchema({
              family: parent.family,
              categoryName: parent.primary_category_name || parent.primary_category || 'LED lighting',
              description: familySummary,
              url: window.location.href,
              image: parent.primary_image,
              models: (articles || variants || []).map((a) => ({
                code: a.base_code || a.order_code || a.sku,
                image: a.primary_image,
                category: a.primary_category_name,
                props: {
                  Power: a.power_numeric || a.power,
                  Dimensions: a.dimension,
                  'Cut-out': a.cut_out,
                  'IP rating': a.ip_rating_normalized || a.ip_rating,
                },
              })).filter((m) => m.code),
            }),
          ]}
        />
        <style>{`
          @keyframes fadeUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
          .animate-fadeUp { animation: fadeUp 0.6s cubic-bezier(0.25,0.46,0.45,0.94); }
        `}</style>

        {/* Breadcrumb */}
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/" className="text-xs text-[#999] hover:text-[#1A1A1A]">Home</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator className="text-[#DDD]" />
              <BreadcrumbItem><BreadcrumbLink href="/products" className="text-xs text-[#999] hover:text-[#1A1A1A]">Products</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator className="text-[#DDD]" />
              <BreadcrumbItem><BreadcrumbLink href={`/${collectionRoute}`} className="text-xs text-[#999] hover:text-[#1A1A1A]">{collectionLabel}</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator className="text-[#DDD]" />
              <BreadcrumbItem><BreadcrumbPage className="text-xs font-medium text-[#1A1A1A]">{parent.family}</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <CatalogueIntro scope={{collection: collectionRoute, familyName: parent.family, category: parent.primary_category || parent.category_slug}} titleTestId="family-name" />
        {/* Header */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-8">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3" data-testid="family-category-label">{parent.primary_category_name || parent.primary_category}</p>
            <h2 className="sr-only" data-testid="family-detail-heading">
              <span style={{ fontWeight: 300 }}>{displayNameParts[0]} </span>
              <span style={{ fontWeight: 700 }}>{displayNameParts.slice(1).join(' ') || ''}</span>
            </h2>
          </FadeUp>
          <FadeUp delay={100}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-[#777]">{articles.length} product line{articles.length !== 1 ? 's' : ''}</span>
              <span className="text-[#DDD]">·</span>
              <span className="text-sm text-[#777]">{totalSkuCount} total variants</span>
              {parent.object_class && (<><span className="text-[#DDD]">·</span><span className="text-xs font-medium uppercase tracking-wider text-[#999]">{parent.object_class.replace(/_/g, ' ')}</span></>)}
              {parent.mounting_type_slug && (<><span className="text-[#DDD]">·</span><span className="text-xs font-medium uppercase tracking-wider text-[#999]">{parent.mounting_type_slug.replace(/-/g, ' ')}</span></>)}
            </div>
          </FadeUp>
        </section>

        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-10"><FamilyCatalogueDescription identity={identity} /></section>
        {/* Specifier conversion: sample, one-PDF spec pack, BOQ pricing */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-12">
          <FadeUp delay={150}>
            <SpecifierActions
              articles={articles || variants}
              family={parent.family}
              collection={parent.collection_slug}
            />
          </FadeUp>
        </section>

        {/* Full specification (2026 audit family template) */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 border-t border-[#E8E8E8]">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Technical Data</p>
            <h2 className="text-3xl md:text-4xl tracking-tight uppercase mb-8" style={{ fontFamily: 'var(--font-display)' }}>
              <span style={{ fontWeight: 300 }}>Full </span><span style={{ fontWeight: 700 }}>Specification</span>
            </h2>
            <FamilySpecTable parent={parent} articles={articles || variants} />
          <FamilyEditorial editorial={editorial} guide={guide} />
          </FadeUp>
        </section>

        {/* Configuration filter — narrows the product-lines grid below by
            spec, giving multi-article families (COB Strip, Flexi Grazer,
            SMD 2835/3528/5050 etc.) the same configurator experience as
            single-article families. */}
        {configuratorAttrs.length > 0 && (
          <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 border-b border-[#E8E8E8]">
            {configuratorBlock}
          </section>
        )}

        {/* Product Lines Grid */}
        <section className="bg-[#FAFAFA] py-16">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <FadeUp>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Product Lines</p>
              <h2 className="text-3xl md:text-4xl tracking-tight uppercase mb-12" style={{ fontFamily: 'var(--font-display)' }}>
                <span style={{ fontWeight: 300 }}>{filteredArticles.length} </span><span style={{ fontWeight: 700 }}>{hasSelections ? `of ${articles.length} Products` : 'Products'}</span>
              </h2>
            </FadeUp>

            {hasSelections && filteredArticles.length === 0 && (
              <div className="mb-8 bg-[#FFF8F0] border border-[#F0E0C8] rounded-lg p-4" data-testid="unavailable-warning">
                <p className="text-sm font-semibold text-[#8B6914]">No product line matches this configuration</p>
                <p className="text-xs text-[#A08040] mt-1">Try adjusting your selection above.</p>
              </div>
            )}

            <div className="balanced-grid article-grid" data-testid="multi-article-grid">
              {filteredArticles.map((art, idx) => (
                <FadeUp key={art.base_code} delay={idx * 60}>
                  <Link
                    to={`/${collectionRoute}/${familyParam}/${modelSlug(art.base_code)}`}
                    className="group block"
                    data-testid={`article-card-${art.base_code}`}
                  >
                    <div className="bg-white border border-[#EAEAEA] rounded-lg overflow-hidden hover:shadow-lg hover:border-[#DDD] transition-all duration-300">
                      <div className="aspect-square bg-[#F8F8F8] p-4 flex items-center justify-center relative">
                        <ProductImage
                      src={art.primary_image}
                      alt={`LUMIBRIGHT ${parent.family} ${art.base_code} ${(parent.primary_category_name || parent.primary_category || 'luminaire').toLowerCase()} product view`}
                      className="p-1"
                    />
                        {art.sku_count > 1 && (
                          <span className="absolute top-2 right-2 text-[9px] font-semibold bg-[#1A1A1A] text-white px-2 py-0.5 rounded-full">
                            {art.sku_count} var.
                          </span>
                        )}
                        {hasMultipleCollections && art.collection_slug && (
                          <span className={`absolute top-2 left-2 text-[8px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${
                            art.collection_slug === 'lumi-outdoor' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {art.collection_slug.replace('lumi-', '')}
                          </span>
                        )}
                      </div>
                      <div className="p-4 border-t border-[#F0F0F0]">
                        <p className="text-sm font-bold text-[#1A1A1A] tracking-wide" data-testid={`article-code-${art.base_code}`}>{art.base_code}</p>
                        <p className="text-[11px] text-[#999] mt-0.5 line-clamp-1">{art.name}</p>
                        <div className="flex gap-1.5 mt-3 flex-wrap">
                          {art.powers.size > 0 && (
                            <span className="text-[9px] font-semibold bg-[#F0F0F0] text-[#666] px-2 py-0.5 rounded">
                              {[...art.powers].sort((a, b) => a - b).map(p => `${p}W`).join(' / ')}
                            </span>
                          )}
                          {art.colors.size > 0 && (
                            <span className="text-[9px] font-medium bg-[#F0F0F0] text-[#666] px-1.5 py-0.5 rounded">
                              {[...art.colors].join('/')}
                            </span>
                          )}
                          {art.beams.size > 0 && (
                            <span className="text-[9px] font-medium bg-[#F0F0F0] text-[#666] px-1.5 py-0.5 rounded">
                              {[...art.beams].sort((a, b) => a - b).map(b => `${b}°`).join('/')}
                            </span>
                          )}
                          {art.ips.size > 0 && (
                            <span className="text-[9px] font-medium bg-[#F0F0F0] text-[#666] px-1.5 py-0.5 rounded">
                              {[...art.ips].join('/')}
                            </span>
                          )}
                          {art.dims.size > 0 && (
                            <span className="text-[9px] font-medium bg-[#F0F0F0] text-[#666] px-1.5 py-0.5 rounded font-mono" data-testid={`article-size-${art.base_code}`}>
                              {[...art.dims][0]}
                            </span>
                          )}
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <span className="text-[10px] text-[#BBB]">{art.sku_count} variant{art.sku_count !== 1 ? 's' : ''}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-[#CCC] group-hover:text-[#1A1A1A] transition-colors duration-300" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>


        <DimensionSection {...dimensionInfo} />


        {/* Specs At a Glance */}
        {(() => {
          const specs = [
            attrOptions.power?.length > 0 && { icon: Zap, label: attrOptions.power.length > 1 ? `${Math.min(...attrOptions.power)}–${Math.max(...attrOptions.power)}W` : `${attrOptions.power[0]}W`, desc: 'Power Range' },
            attrOptions.beam_angle?.length > 0 && { icon: Eye, label: `${attrOptions.beam_angle.length} options`, desc: `Beam: ${attrOptions.beam_angle.map(b => `${b}°`).join(', ')}` },
            attrOptions.cct?.length > 0 && { icon: Thermometer, label: `${attrOptions.cct.length} CCT`, desc: attrOptions.cct.map(c => `${c}K`).join(', ') },
            attrOptions.color?.length > 0 && { icon: Droplets, label: attrOptions.color.join(' / '), desc: 'Colour Options' },
            attrOptions.cri?.length > 0 && { icon: Lightbulb, label: `CRI ${attrOptions.cri.join('/')}`, desc: 'Colour Rendering' },
            attrOptions.ip_rating?.length > 0 && { icon: Shield, label: attrOptions.ip_rating.join(' / '), desc: 'Protection Rating' },
            attrOptions.body_finish?.length > 0 && { icon: Palette, label: `${attrOptions.body_finish.length} finishes`, desc: 'Body Finish Options' },
            attrOptions.dimension?.length > 0 && { icon: Ruler, label: attrOptions.dimension.length > 1 ? `${attrOptions.dimension.length} sizes` : attrOptions.dimension[0], desc: attrOptions.dimension.length > 1 ? attrOptions.dimension.join(', ') : 'Dimensions' },
            attrOptions.cut_out?.length > 0 && { icon: Scissors, label: attrOptions.cut_out.length > 1 ? `${attrOptions.cut_out.length} cut-outs` : attrOptions.cut_out[0], desc: attrOptions.cut_out.length > 1 ? attrOptions.cut_out.join(', ') : 'Cut-Out' },
          ].filter(Boolean);
          if (specs.length === 0) return null;
          return (
            <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
              <FadeUp>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Specifications</p>
                <h2 className="text-3xl md:text-4xl tracking-tight uppercase mb-12" style={{ fontFamily: 'var(--font-display)' }}>
                  <span style={{ fontWeight: 300 }}>At a </span><span style={{ fontWeight: 700 }}>Glance</span>
                </h2>
              </FadeUp>
              <div className="inline-grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-y divide-[#E8E8E8] border border-[#E8E8E8]" data-testid="family-specs-grid">
                {specs.map((spec, i) => (
                  <FadeUp key={i} delay={i * 60} className="bg-white p-6 flex flex-col gap-2 group hover:bg-[#FAFAFA] transition-colors duration-300">
                    <spec.icon className="w-4 h-4 text-[#BBB] group-hover:text-[#1A1A1A] transition-colors duration-300" />
                    <p className="text-base md:text-lg font-bold tracking-tight break-words" style={{ fontFamily: 'var(--font-display)' }}>{spec.label}</p>
                    <p className="text-xs text-[#999] break-words">{spec.desc}</p>
                  </FadeUp>
                ))}
              </div>
            </section>
          );
        })()}

        <FamilyModelConfigurator variants={variants} collection={collectionRoute} family={familyParam} />
        <FamilyComponents components={data?.related_components} />
        {/* Downloads */}
        {datasheets.total > 0 && (
          <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8 border-t border-[#E8E8E8]" data-testid="downloads-section">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-6">Downloads</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {datasheets.family_datasheets.map(ds => (
                <a key={ds.datasheet_id} href={ds.file_url} target="_blank" rel="noopener noreferrer" className="group" data-testid={`datasheet-${ds.datasheet_id}`}>
                  <div className="bg-white border border-[#EAEAEA] rounded-lg p-4 flex items-center gap-3 hover:shadow-md transition-all duration-300">
                    <FileText className="w-8 h-8 text-[#BBB] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate group-hover:text-[#1A1A1A]">{ds.file_name}</p>
                      <p className="text-xs text-[#999]">{ds.description || ds.file_type}</p>
                    </div>
                    <Download className="w-4 h-4 text-[#CCC] group-hover:text-[#1A1A1A]" />
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* PDF Download CTA */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8">
          <FadeUp>
            <Button
              onClick={downloadProductPdf}
              disabled={generatingPdf}
              className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-none px-8 py-5 text-xs font-bold uppercase tracking-wider"
              data-testid="download-product-pdf"
            >
              {generatingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              {generatingPdf ? 'Generating...' : 'Download Family Overview PDF'}
            </Button>
          </FadeUp>
        </section>

        {/* Compatible Accessories */}
        {accessories.total > 0 && <AccessoriesSection accessories={accessories} collectionRoute={collectionRoute} />}

        {/* Sibling Families */}
        {siblings.length > 0 && <SiblingsSection siblings={siblings} collectionRoute={collectionRoute} collectionLabel={collectionLabel} />}
      </div>
    );
  }

  // ─── SINGLE-ARTICLE FAMILY ───
  return (
    <div className="bg-white text-[#1A1A1A] min-h-screen" data-testid="family-detail-page">
      <SEO
        identity={identity}
        title={variantSeo?.title || `${parent.family} - ${parent.primary_category_name || parent.primary_category} - ${collectionLabel}`}
        description={variantSeo?.description || `${parent.family} LED ${parent.primary_category_name || ''}. ${data.variant_count} variants. ${attrOptions.power?.length > 0 ? `Power: ${Math.min(...attrOptions.power)}-${Math.max(...attrOptions.power)}W.` : ''}`}
        image={variantSeo?.image || parent.primary_image}
        schema={[
          generateBreadcrumbSchema([
            { name: 'Home', url: `${window.location.origin}/` },
            { name: 'Products', url: `${window.location.origin}/products` },
            { name: collectionLabel, url: `${window.location.origin}/${collectionRoute}` },
            { name: parent.family, url: window.location.href },
          ]),
          // SCHEMA01: family pages describe a set of separate models
          generateFamilyCollectionSchema({
            family: parent.family,
            categoryName: parent.primary_category_name || parent.primary_category || 'LED lighting',
            description: parent.description || familySummary,
            url: window.location.href,
            image: parent.primary_image,
            models: (articles || variants || []).map((a) => ({
              code: a.base_code || a.order_code || a.sku,
              image: a.primary_image,
              category: a.primary_category_name,
              props: {
                Power: a.power_numeric || a.power,
                Dimensions: a.dimension,
                'Cut-out': a.cut_out,
                'IP rating': a.ip_rating_normalized || a.ip_rating,
              },
            })).filter((m) => m.code),
          }),
        ]}
      />
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        .animate-fadeUp { animation: fadeUp 0.6s cubic-bezier(0.25,0.46,0.45,0.94); }
      `}</style>

      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink href="/" className="text-xs text-[#999] hover:text-[#1A1A1A]">Home</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator className="text-[#DDD]" />
            <BreadcrumbItem><BreadcrumbLink href="/products" className="text-xs text-[#999] hover:text-[#1A1A1A]">Products</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator className="text-[#DDD]" />
            <BreadcrumbItem><BreadcrumbLink href={`/${collectionRoute}`} className="text-xs text-[#999] hover:text-[#1A1A1A]">{collectionLabel}</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator className="text-[#DDD]" />
            <BreadcrumbItem><BreadcrumbPage className="text-xs font-medium text-[#1A1A1A]">{parent.family}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <CatalogueIntro scope={{collection: collectionRoute, familyName: parent.family, category: parent.primary_category || parent.category_slug}} titleTestId="family-name" />
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-8">
        <FadeUp>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3" data-testid="family-category-label">{parent.primary_category_name || parent.primary_category}</p>
          <h2 className="sr-only" data-testid="family-detail-heading">
            <span style={{ fontWeight: 300 }}>{displayNameParts[0]} </span>
            <span style={{ fontWeight: 700 }}>{displayNameParts.slice(1).join(' ') || ''}</span>
          </h2>
        </FadeUp>
        <FadeUp delay={100}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-[#777]">{data.variant_count} variant{data.variant_count !== 1 ? 's' : ''}</span>
            {parent.object_class && (<><span className="text-[#DDD]">·</span><span className="text-xs font-medium uppercase tracking-wider text-[#999]">{parent.object_class.replace(/_/g, ' ')}</span></>)}
            {parent.mounting_type_slug && (<><span className="text-[#DDD]">·</span><span className="text-xs font-medium uppercase tracking-wider text-[#999]">{parent.mounting_type_slug.replace(/-/g, ' ')}</span></>)}
          </div>
        </FadeUp>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-10"><FamilyCatalogueDescription identity={identity} /></section>
      {/* Product Image + Configurator */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

          {/* Left: Product Image Gallery */}
          <div className="lg:col-span-5">
            <FadeUp>
              <div className="bg-[#F8F8F8] rounded-lg flex items-center justify-center p-6" style={{ aspectRatio: '4/3' }} data-testid="product-hero-image">
                <ProductImage
                  src={galleryImages[activeImageIndex] || selectedVariant?.primary_image || parent.primary_image}
                  alt={`LUMIBRIGHT ${parent.family} ${(parent.primary_category_name || parent.primary_category || 'luminaire').toLowerCase()} product view`}
                  className="p-2"
                />
              </div>
              {/* Thumbnail strip */}
              {galleryImages.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="image-gallery-thumbnails">
                  {galleryImages.map((img, idx) => {
                    const isScene = img.toLowerCase().includes('_scene.') || img.toLowerCase().includes('_app.');
                    return (
                      <button
                        key={idx}
                        onClick={() => setActiveImageIndex(idx)}
                        className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden transition-all ${idx === activeImageIndex ? 'border-[#1A1A1A] shadow-sm' : 'border-[#E8E8E8] hover:border-[#BBB]'}`}
                        data-testid={`gallery-thumb-${idx}`}
                      >
                        <img
                          src={normalizeImagePath(img)}
                          alt={`LUMIBRIGHT ${parent.family} ${(parent.primary_category_name || parent.primary_category || 'luminaire').toLowerCase()} ${isScene ? 'installation photograph' : 'product view'} ${idx + 1}`}
                          className="w-full h-full object-contain bg-[#FAFAFA] p-1"
                          loading="lazy"
                          onError={(e) => { e.target.style.opacity = '0.3'; }}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Image type label */}
              {galleryImages.length > 1 && galleryImages[activeImageIndex] && (
                <p className="text-[10px] uppercase tracking-wider text-[#AAA] mt-1">
                  {galleryImages[activeImageIndex].toLowerCase().includes('_scene.') || galleryImages[activeImageIndex].toLowerCase().includes('_app.')
                    ? 'Application View'
                    : 'Product View'}
                </p>
              )}
            </FadeUp>

            {/* Selected variant info */}
            {selectedVariant && (
              <FadeUp delay={100}>
                <div className="mt-6 bg-[#FAFAFA] border border-[#EAEAEA] rounded-lg p-5" data-testid="selected-variant-card">
                  <div className="flex items-center gap-2 mb-3">
                    <Check className="w-4 h-4 text-[#1A1A1A]" />
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#999]">Selected Variant</p>
                  </div>
                  <p className="text-lg font-bold tracking-tight">{selectedVariant.base_code}</p>
                  <p className="text-xs text-[#999] mt-1 line-clamp-2">{selectedVariant.name}</p>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {selectedVariant.power_numeric && <div className="text-xs"><span className="text-[#999]">Power</span> <span className="font-semibold ml-1">{selectedVariant.power_numeric}W</span></div>}
                    {selectedVariant.beam_angle_numeric && <div className="text-xs"><span className="text-[#999]">Beam</span> <span className="font-semibold ml-1">{selectedVariant.beam_angle_numeric}°</span></div>}
                    {selectedVariant.cct_values?.length > 0 && <div className="text-xs"><span className="text-[#999]">CCT</span> <span className="font-semibold ml-1">{selectedVariant.cct_values.map(c => `${c}K`).join(', ')}</span></div>}
                    {selectedVariant.ip_rating_normalized && <div className="text-xs"><span className="text-[#999]">IP</span> <span className="font-semibold ml-1">{selectedVariant.ip_rating_normalized}</span></div>}
                    {selectedVariant.body_finish_normalized && <div className="text-xs"><span className="text-[#999]">Finish</span> <span className="font-semibold ml-1 capitalize">{selectedVariant.body_finish_normalized}</span></div>}
                    {selectedVariant.cri_numeric && <div className="text-xs"><span className="text-[#999]">CRI</span> <span className="font-semibold ml-1">{selectedVariant.cri_numeric}</span></div>}
                  </div>
                  <Separator className="my-4 bg-[#E8E8E8]" />
                  <Button onClick={() => downloadVariantPdf(selectedVariant)} className="w-full bg-[#1A1A1A] text-white hover:bg-[#333] rounded-none text-xs font-bold uppercase tracking-wider" data-testid="download-variant-pdf">
                    <FileDown className="w-3.5 h-3.5 mr-2" /> Download Datasheet
                  </Button>
                </div>
              </FadeUp>
            )}
          </div>

          {/* Right: Configurator */}
          <div className="lg:col-span-7">
            {configuratorBlock}

            {/* Variant list - compact for single article */}
            <div className="mt-12">
              <div className="flex items-center justify-between mb-6">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999]">
                  {hasSelections ? `Matching Variants (${matchedVariants.length})` : `All Variants (${variants.length})`}
                </p>
              </div>

              <div className="border border-[#EAEAEA] rounded-lg overflow-hidden" data-testid="variant-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#FAFAFA] border-b border-[#E8E8E8]">
                        <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#999]">Code</th>
                        {showPowerCol && <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#999]">Power</th>}
                        {showCctCol && <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#999]">CCT</th>}
                        {showBeamCol && <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#999]">Beam</th>}
                        {showCriCol && <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#999]">CRI</th>}
                        {showFinishCol && <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#999]">Finish</th>}
                        {showSizeCol && <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#999]">Size</th>}
                        <th className="text-center px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#999]">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(hasSelections ? matchedVariants : variants).length === 0 ? (
                        <tr><td colSpan={visibleColCount} className="text-center py-12 text-[#999]">No variants match. Try removing a selection.</td></tr>
                      ) : (
                        (hasSelections ? matchedVariants : variants).map((v) => {
                          const isHighlighted = selectedVariant?.product_id === v.product_id;
                          const variantSlug = modelSlug(v.base_code || v.product_id);
                          return (
                            <tr key={v.product_id} className={`border-b border-[#F0F0F0] transition-colors ${isHighlighted ? 'bg-[#F8F8F0]' : 'hover:bg-[#FAFAFA]'}`} data-testid={`variant-row-${v.base_code || v.product_id}`}>
                              <td className="px-4 py-3 font-mono text-xs font-semibold">
                                {isHighlighted && <Check className="w-3 h-3 inline mr-1.5 text-green-600" />}
                                <Link to={`/${collectionRoute}/${familyParam}/${variantSlug}`} className="hover:underline" data-testid={`variant-link-${v.base_code || v.product_id}`}>
                                  {v.base_code || '—'}
                                </Link>
                              </td>
                              {showPowerCol && <td className="px-4 py-3 text-xs">{v.power_numeric ? `${v.power_numeric}W` : '—'}</td>}
                              {showCctCol && <td className="px-4 py-3 text-xs">{v.cct_values?.length > 0 ? v.cct_values.map(c => `${c}K`).join(', ') : (v.color_values?.length > 0 ? v.color_values.join(', ') : '—')}</td>}
                              {showBeamCol && <td className="px-4 py-3 text-xs">{v.beam_angle_numeric ? `${v.beam_angle_numeric}°` : '—'}</td>}
                              {showCriCol && <td className="px-4 py-3 text-xs">{v.cri_numeric || '—'}</td>}
                              {showFinishCol && <td className="px-4 py-3 text-xs capitalize">{v.body_finish_normalized || '—'}</td>}
                              {showSizeCol && <td className="px-4 py-3 text-xs font-mono" data-testid={`variant-size-${v.base_code || v.product_id}`}>{v.dimension || '—'}</td>}
                              <td className="px-4 py-3 text-center">
                                <button onClick={() => downloadVariantPdf(v)} className="text-[#CCC] hover:text-[#1A1A1A] transition-colors p-1" title={`Download ${v.base_code || 'variant'} datasheet`} data-testid={`variant-pdf-${v.base_code || v.product_id}`}>
                                  <FileDown className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Specs summary when no variant selected */}
            {!selectedVariant && (
              <FadeUp delay={200}>
                <div className="mt-8">
                  <Button onClick={downloadProductPdf} disabled={generatingPdf} className="bg-[#1A1A1A] text-white hover:bg-[#333] rounded-none px-8 py-5 text-xs font-bold uppercase tracking-wider" data-testid="download-product-pdf">
                    {generatingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                    {generatingPdf ? 'Generating...' : 'Download Product Overview PDF'}
                  </Button>
                </div>
              </FadeUp>
            )}
          </div>
        </div>
      </section>

      <DimensionSection {...dimensionInfo} />

      {/* Specifier conversion + full specification — single-article family */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
        <SpecifierActions
          articles={articles || variants}
          family={parent.family}
          collection={parent.collection_slug}
        />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-12 border-b border-[#E8E8E8]">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Technical Data</p>
        <h2 className="text-3xl md:text-4xl tracking-tight uppercase mb-8" style={{ fontFamily: 'var(--font-display)' }}>
          <span style={{ fontWeight: 300 }}>Full </span><span style={{ fontWeight: 700 }}>Specification</span>
        </h2>
        <FamilySpecTable parent={parent} articles={articles || variants} />
          <FamilyEditorial editorial={editorial} guide={guide} />
      </section>

      {/* Specs At a Glance — single-article family */}
      {(() => {
        const specs = [
          attrOptions.power?.length > 0 && { icon: Zap, label: attrOptions.power.length > 1 ? `${Math.min(...attrOptions.power)}–${Math.max(...attrOptions.power)}W` : `${attrOptions.power[0]}W`, desc: 'Power' },
          attrOptions.beam_angle?.length > 0 && { icon: Eye, label: attrOptions.beam_angle.length > 1 ? `${attrOptions.beam_angle.length} options` : `${attrOptions.beam_angle[0]}°`, desc: 'Beam Angle' },
          attrOptions.cct?.length > 0 && { icon: Thermometer, label: attrOptions.cct.length > 1 ? `${attrOptions.cct.length} CCT` : `${attrOptions.cct[0]}K`, desc: attrOptions.cct.length > 1 ? attrOptions.cct.map(c => `${c}K`).join(', ') : 'Colour Temperature' },
          attrOptions.color?.length > 0 && { icon: Droplets, label: attrOptions.color.join(' / '), desc: 'Colour Options' },
          attrOptions.cri?.length > 0 && { icon: Lightbulb, label: `CRI ${attrOptions.cri.join('/')}`, desc: 'Colour Rendering' },
          attrOptions.ip_rating?.length > 0 && { icon: Shield, label: attrOptions.ip_rating.join(' / '), desc: 'Protection Rating' },
          attrOptions.body_finish?.length > 0 && { icon: Palette, label: attrOptions.body_finish.length > 1 ? `${attrOptions.body_finish.length} finishes` : attrOptions.body_finish[0], desc: 'Body Finish' },
          attrOptions.dimension?.length > 0 && { icon: Ruler, label: attrOptions.dimension.length > 1 ? `${attrOptions.dimension.length} sizes` : attrOptions.dimension[0], desc: attrOptions.dimension.length > 1 ? attrOptions.dimension.join(', ') : 'Dimensions' },
          attrOptions.cut_out?.length > 0 && { icon: Scissors, label: attrOptions.cut_out.length > 1 ? `${attrOptions.cut_out.length} cut-outs` : attrOptions.cut_out[0], desc: attrOptions.cut_out.length > 1 ? attrOptions.cut_out.join(', ') : 'Cut-Out' },
        ].filter(Boolean);
        if (specs.length === 0) return null;
        return (
          <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 border-t border-[#E8E8E8]">
            <FadeUp>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Specifications</p>
              <h2 className="text-3xl md:text-4xl tracking-tight uppercase mb-12" style={{ fontFamily: 'var(--font-display)' }}>
                <span style={{ fontWeight: 300 }}>At a </span><span style={{ fontWeight: 700 }}>Glance</span>
              </h2>
            </FadeUp>
            <div className="inline-grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-y divide-[#E8E8E8] border border-[#E8E8E8]" data-testid="single-article-specs-grid">
              {specs.map((spec, i) => (
                <FadeUp key={i} delay={i * 60} className="bg-white p-6 flex flex-col gap-2 group hover:bg-[#FAFAFA] transition-colors duration-300">
                  <spec.icon className="w-4 h-4 text-[#BBB] group-hover:text-[#1A1A1A] transition-colors duration-300" />
                  <p className="text-base md:text-lg font-bold tracking-tight break-words" style={{ fontFamily: 'var(--font-display)' }}>{spec.label}</p>
                  <p className="text-xs text-[#999] break-words">{spec.desc}</p>
                </FadeUp>
              ))}
            </div>
          </section>
        );
      })()}

      <FamilyModelConfigurator variants={variants} collection={collectionRoute} family={familyParam} />
      <FamilyComponents components={data?.related_components} />
      {/* Downloads */}
      {datasheets.total > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8 border-t border-[#E8E8E8]" data-testid="downloads-section">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-6">Downloads</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {datasheets.family_datasheets.map(ds => (
              <a key={ds.datasheet_id} href={ds.file_url} target="_blank" rel="noopener noreferrer" className="group" data-testid={`datasheet-${ds.datasheet_id}`}>
                <div className="bg-white border border-[#EAEAEA] rounded-lg p-4 flex items-center gap-3 hover:shadow-md transition-all duration-300">
                  <FileText className="w-8 h-8 text-[#BBB] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{ds.file_name}</p>
                    <p className="text-xs text-[#999]">{ds.description || ds.file_type}</p>
                  </div>
                  <Download className="w-4 h-4 text-[#CCC] group-hover:text-[#1A1A1A]" />
                </div>
              </a>
            ))}
            {datasheets.variant_datasheets.map(ds => {
              const isForSelected = selectedVariant && ds.product_id === selectedVariant.product_id;
              return (
                <a key={ds.datasheet_id} href={ds.file_url} target="_blank" rel="noopener noreferrer" className="group" data-testid={`datasheet-variant-${ds.datasheet_id}`}>
                  <div className={`bg-white border rounded-lg p-4 flex items-center gap-3 transition-all duration-300 ${isForSelected ? 'border-[#1A1A1A] shadow-sm' : 'border-[#EAEAEA] hover:shadow-md'}`}>
                    <FileText className="w-8 h-8 text-[#BBB] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{ds.file_name}</p>
                      <p className="text-xs text-[#999]">{ds.file_type} · Variant</p>
                    </div>
                    <Download className="w-4 h-4 text-[#CCC] group-hover:text-[#1A1A1A]" />
                  </div>
                </a>
              );
            })}
          </div>
        </section>
      )}

      {/* Compatible Accessories */}
      {accessories.total > 0 && <AccessoriesSection accessories={accessories} collectionRoute={collectionRoute} />}

      {/* Sibling Families */}
      {siblings.length > 0 && <SiblingsSection siblings={siblings} collectionRoute={collectionRoute} collectionLabel={collectionLabel} />}
    </div>
  );
}

/* ── Accessories Section ── */
function AccessoriesSection({ accessories, collectionRoute }) {
  const tiers = [
    { items: accessories.required, label: 'Required', borderClass: 'border-red-200 hover:border-red-300' },
    { items: accessories.recommended, label: 'Recommended', borderClass: 'border-amber-200 hover:border-amber-300' },
    { items: accessories.compatible, label: (accessories.required.length > 0 || accessories.recommended.length > 0) ? 'Also Compatible' : null, borderClass: 'hover:shadow-md' },
  ].filter(tier => tier.items.length > 0);

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 border-t border-[#E8E8E8]" data-testid="compatible-accessories-section">
      <FadeUp>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Accessories</p>
        <h2 className="text-3xl tracking-tight uppercase mb-12" style={{ fontFamily: 'var(--font-display)' }}>
          <span style={{ fontWeight: 300 }}>Compatible </span><span style={{ fontWeight: 700 }}>Components</span>
        </h2>
      </FadeUp>
      {tiers.map((tier, tierIdx) => (
        <div key={tierIdx} className="mb-8">
          {tier.label && <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#999] mb-4">{tier.label}</p>}
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {tier.items.map(acc => (
              <Link key={acc.canonical_slug} to={`/${acc.primary_collection || collectionRoute}/${acc.canonical_slug}`} className="group" data-testid={`acc-card-${acc.canonical_slug}`}>
                <div className={`bg-white border border-[#EAEAEA] rounded-lg overflow-hidden transition-all duration-300 ${tier.borderClass}`}>
                  <div className="aspect-square bg-[#F8F8F8] p-3 flex items-center justify-center">
                    <ProductImage src={acc.primary_image} alt={acc.name || acc.family} className="p-1" />
                  </div>
                  <div className="p-3 border-t border-[#F0F0F0]">
                    <p className="text-xs font-bold text-[#1A1A1A] group-hover:text-[#555] transition-colors truncate">{acc.name || acc.family}</p>
                    <p className="text-[10px] text-[#999] mt-0.5 truncate">{acc.primary_category_name || acc.object_class}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

/* ── Dimension Drawing ── */
function DimensionSection({ diagram, dimension, cutOut, modelCode, isModelSpecific, modelCount }) {
  if (!diagram && !dimension) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 border-t border-[#E8E8E8]" data-testid="dimension-section">
      <FadeUp>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Dimensions</p>
        <h2 className="text-3xl md:text-4xl tracking-tight uppercase mb-10" style={{ fontFamily: 'var(--font-display)' }}>
          <span style={{ fontWeight: 300 }}>Technical </span><span style={{ fontWeight: 700 }}>Drawing</span>
        </h2>
        {modelCode && (
          <p className="text-sm text-[#666] mb-8 max-w-2xl" data-testid="dimension-model-note">
            Drawing and sizes shown for model <span className="font-mono font-semibold">{modelCode}</span>
            {isModelSpecific
              ? `. This family has ${modelCount} models with different dimensions — select a model above, or open its row in the variant table, for its own drawing.`
              : '.'}
          </p>
        )}
      </FadeUp>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        {diagram && (
          <div className="lg:col-span-5 bg-white border border-[#E8E8E8] p-6 flex items-center justify-center">
            <img src={diagram} alt={`${modelCode ? `LUMIBRIGHT ${modelCode}` : 'Product'} dimension drawing${dimension ? ` — ${dimension}` : ''}`} className="max-h-[320px] w-auto object-contain" loading="lazy" data-testid="dimension-diagram" />
          </div>
        )}
        <div className="lg:col-span-4 space-y-4">
          {dimension && (
            <div className="border-l-2 border-[#1A1A1A] pl-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#999] mb-1">
                {modelCode ? `Size — model ${modelCode}` : 'Product Size'}
              </p>
              <p className="text-lg font-mono font-semibold" data-testid="dimension-value">{dimension}</p>
            </div>
          )}
          {cutOut && (
            <div className="border-l-2 border-[#DDD] pl-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#999] mb-1">Cut-Out</p>
              <p className="text-lg font-mono font-semibold" data-testid="cutout-value">{cutOut}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ── Siblings Section ── */
function SiblingsSection({ siblings, collectionRoute, collectionLabel }) {
  return (
    <section className="bg-[#FAFAFA] py-16" data-testid="sibling-families">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <FadeUp>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Related</p>
          <h2 className="text-3xl tracking-tight uppercase mb-12" style={{ fontFamily: 'var(--font-display)' }}>
            <span style={{ fontWeight: 300 }}>More from </span><span style={{ fontWeight: 700 }}>{collectionLabel}</span>
          </h2>
        </FadeUp>
        <div className="balanced-grid article-grid" data-testid="related-family-grid">
          {siblings.map((sib, i) => (
            <FadeUp key={sib.family_slug || sib.canonical_slug} delay={i * 50}>
              <Link to={`/${collectionRoute}/${sib.family_slug || sib.canonical_slug}`} className="group block" data-testid={`sibling-family-${sib.family_slug || sib.canonical_slug}`}>
                <div className="bg-white border border-[#EAEAEA] rounded-lg overflow-hidden hover:shadow-lg hover:border-[#DDD] transition-all duration-300">
                  <div className="aspect-square bg-[#F8F8F8] p-4 flex items-center justify-center">
                    <ProductImage
                      src={sib.primary_image}
                      alt={`LUMIBRIGHT ${sib.family} ${(sib.primary_category_name || 'luminaire').toLowerCase()} product view`}
                      className="p-2"
                    />
                  </div>
                  <div className="p-3 border-t border-[#F0F0F0]">
                    <p className="text-xs font-bold text-[#1A1A1A] tracking-wide group-hover:text-[#555] transition-colors">{sib.family}</p>
                    <p className="text-[10px] text-[#999] mt-0.5">{sib.variant_count} variant{sib.variant_count !== 1 ? 's' : ''}</p>
                    {sib.match_reason && (
                      <p
                        className="text-[10px] text-[#A08040] mt-1 font-medium"
                        data-testid={`sibling-reason-${sib.family_slug || sib.canonical_slug}`}
                      >
                        {sib.match_reason}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
